import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Asks Powens to fetch the banks now.
 *
 * Left alone, Powens refreshes a connection on its own cycle — every 24h by
 * default — so a card payment made this morning can stay invisible until
 * tomorrow. This is the user-facing escape hatch from that cycle.
 *
 * It returns as soon as Powens accepts the job, not when the data lands: the
 * bank takes 30 to 45 seconds to answer, and the webhook imports the result.
 * The accounts page polls, so the rows appear on their own.
 */
export function usePowensRefresh() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const refresh = useCallback(
    async (connectionId?: number) => {
      setIsRefreshing(true);
      try {
        const { data, error } = await supabase.functions.invoke('powens-refresh', {
          body: connectionId ? { connection_id: connectionId } : {},
        });

        if (error) throw new Error(error.message);
        if (data?.error) throw new Error(data.error);

        toast({
          title: t('powens.refreshStarted'),
          description: t('powens.refreshStartedDesc'),
        });

        // Give the poll something to find: the import lands via the webhook a
        // moment from now, and the page reloads itself when it does.
        window.dispatchEvent(new CustomEvent('accounts-need-refresh'));
        return { success: true, refreshed: data?.refreshed ?? 0 };
      } catch (err) {
        toast({
          title: t('powens.errorTitle'),
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
        return { success: false, refreshed: 0 };
      } finally {
        setIsRefreshing(false);
      }
    },
    [toast, t]
  );

  return { refresh, isRefreshing };
}
